import { init } from '@rematch/core';
import loadingPlugin from '@rematch/loading';
import persistPlugin from '@rematch/persist';
import storage from 'redux-persist/lib/storage';
import { useDispatch, useSelector } from 'react-redux';
import { createTransform } from 'redux-persist';
import { isSameDay } from 'date-fns';

import { models } from './models';
import { INITIAL_STATE as INITIAL_CCM_CONSOLE_STATE } from './ccm/console';

import type { TState as TCcmConsoleState } from './ccm/console';
import type { RematchDispatch, RematchRootState } from '@rematch/core';
import type { ExtraModelsFromLoading } from '@rematch/loading';
import type { TypedUseSelectorHook } from 'react-redux';
import type { RootModel } from './models';

type FullModel = ExtraModelsFromLoading<RootModel>;

interface ModelsState {
  ccmConsole: TCcmConsoleState;
}

const expireCcmConsoleTransform = createTransform<
  TCcmConsoleState,
  TCcmConsoleState & { _persistedAt?: number },
  ModelsState,
  ModelsState
>(
  // calls for each model from white list
  (inboundState, _key) => {
    if (_key === 'ccmConsole') {
      if ('_persistedAt' in inboundState) {
        return inboundState;
      }

      return {
        ...inboundState,
        _persistedAt: Date.now(),
      };
    }

    return inboundState;
  },
  // calls for each model from white list
  (outboundState, _key) => {
    if (_key === 'ccmConsole') {
      const now = Date.now();
      const persistedAt = outboundState?._persistedAt;

      if (!persistedAt || !isSameDay(now, persistedAt)) {
        return INITIAL_CCM_CONSOLE_STATE;
      }

      const { _persistedAt, ...rest } = outboundState;

      return { ...rest, selectedRows: [] };
    }

    return outboundState;
  },
);

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['ccmConsole'],
  transforms: [expireCcmConsoleTransform],
};

const store = init<RootModel, FullModel>({
  plugins: [loadingPlugin(), persistPlugin(persistConfig)],
  models,
});

export type Store = typeof store;
export type RootDispatch = RematchDispatch<RootModel>;
export type RootState = RematchRootState<RootModel, FullModel>;
export default store;

export type AppRootState = ReturnType<Store['getState']>;
export type AppDispatch = Store['dispatch'];

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<AppRootState> = useSelector;
